<script setup>
// Phase 2.9.8 K*.3 — componente reusavel de Quick Action buttons.
// Fetch das actions configuradas + renderiza grid de botoes + abre modal
// se action tem prompts + dispara POST /execute com o contexto.
//
// Props:
//   contactId (obrigatorio)
//   conversationId (opcional, display_id)
//   kanbanItemId (opcional)
//   context: 'conversation' | 'kanban_detail' | 'kanban_agenda'
//     Filtra server-side quais actions aparecem (via show_in_* flags).
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import QuickActionsAPI from 'dashboard/api/quickActions';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';

const props = defineProps({
  contactId: { type: [Number, String], required: true },
  conversationId: { type: [Number, String], default: null },
  kanbanItemId: { type: [Number, String], default: null },
  appointmentId: { type: [Number, String], default: null },
  context: { type: String, default: 'conversation' }, // conversation | kanban_detail | kanban_agenda
  // SiteUp CRM (2026-09-18): no painel lateral do Kanban o bloco de Ações
  // Rápidas só existe quando há ação configurada. O estado vazio ocupava a
  // primeira dobra do card sem dizer nada. O CTA de configuração passou para o
  // menu de três pontos do painel.
  hideEmptyState: { type: Boolean, default: false },
});

const emit = defineEmits(['executed', 'availability']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { isAdmin } = useAdmin();
const accountId = computed(() => route.params.accountId);

const actions = ref([]);
const loading = ref(true);
const loadError = ref('');
const modalAction = ref(null); // action cujo modal esta aberto
const promptValues = ref({});
const executing = ref(false);
const lastResult = ref(null);
const executeError = ref('');
const promptDialog = ref(null);
let loadToken = 0;
const disposed = ref(false);

// Tailwind color → utility classes
const COLOR_CLASSES = {
  emerald: 'bg-emerald-500 hover:bg-emerald-600 text-white',
  blue: 'bg-blue-500 hover:bg-blue-600 text-white',
  violet: 'bg-violet-500 hover:bg-violet-600 text-white',
  amber: 'bg-amber-500 hover:bg-amber-600 text-white',
  rose: 'bg-rose-500 hover:bg-rose-600 text-white',
  red: 'bg-red-500 hover:bg-red-600 text-white',
  slate: 'bg-n-slate-9 hover:bg-n-solid-3 text-white',
};

// Filtra actions pelo contexto atual
const filtered = computed(() => {
  const prop = `show_in_${props.context}`;
  return actions.value.filter(a => a.enabled && a[prop]);
});

async function load() {
  loadToken += 1;
  const token = loadToken;
  const contactId = props.contactId;
  loading.value = true;
  loadError.value = '';
  try {
    const { data } = await QuickActionsAPI.get();
    if (disposed.value || token !== loadToken || props.contactId !== contactId)
      return;
    actions.value = data.quick_actions || [];
  } catch (_e) {
    if (disposed.value || token !== loadToken || props.contactId !== contactId)
      return;
    actions.value = [];
    loadError.value = t('QUICK_ACTION_RUNNER.LOAD_ERROR');
  } finally {
    if (token === loadToken) loading.value = false;
  }
}
onMounted(load);
watch(() => props.contactId, load);

// Quem embrulha o componente precisa saber se sobrou alguma ação depois do
// filtro de contexto, para esconder o bloco inteiro (título e moldura).
watch(
  [filtered, loading, loadError],
  () => {
    emit('availability', {
      count: filtered.value.length,
      loading: loading.value,
      error: Boolean(loadError.value),
    });
  },
  { immediate: true }
);
onBeforeUnmount(() => {
  disposed.value = true;
  loadToken += 1;
});

function colorClass(a) {
  return COLOR_CLASSES[a.color] || COLOR_CLASSES.slate;
}

function closeModal() {
  if (executing.value) return;
  promptDialog.value?.close();
  modalAction.value = null;
  promptValues.value = {};
  executeError.value = '';
}

async function execute(action, values) {
  if (executing.value) return;
  const context = {
    contactId: props.contactId,
    conversationId: props.conversationId,
    kanbanItemId: props.kanbanItemId,
    appointmentId: props.appointmentId,
    executionContext: props.context,
  };
  executing.value = true;
  executeError.value = '';
  try {
    const { data } = await QuickActionsAPI.execute(action.id, {
      contact_id: props.contactId,
      conversation_id: props.conversationId,
      kanban_item_id: props.kanbanItemId,
      appointment_id: props.appointmentId,
      execution_context: props.context,
      prompt_values: values,
    });
    if (
      disposed.value ||
      context.contactId !== props.contactId ||
      context.conversationId !== props.conversationId ||
      context.kanbanItemId !== props.kanbanItemId ||
      context.appointmentId !== props.appointmentId ||
      context.executionContext !== props.context
    )
      return;
    const results = Array.isArray(data.results) ? data.results : [];
    const failedCount =
      results.filter(result => result.success === false).length ||
      (!data.success && results.length === 0 ? 1 : 0);
    const totalCount = results.length || (failedCount ? 1 : 0);
    lastResult.value = {
      action: action.name,
      success: data.success === true && failedCount === 0,
      failedCount,
      totalCount,
      at: Date.now(),
    };
    emit('executed', { action, result: data });
    executing.value = false;
    closeModal();
  } catch (e) {
    if (
      disposed.value ||
      context.contactId !== props.contactId ||
      context.conversationId !== props.conversationId ||
      context.kanbanItemId !== props.kanbanItemId ||
      context.appointmentId !== props.appointmentId ||
      context.executionContext !== props.context
    )
      return;
    executeError.value =
      e?.response?.data?.error === 'quick_action_disabled'
        ? t('QUICK_ACTION_RUNNER.DISABLED')
        : t('QUICK_ACTION_RUNNER.EXECUTE_ERROR');
  } finally {
    executing.value = false;
  }
}

function clickAction(action) {
  if (executing.value) return;
  executeError.value = '';
  lastResult.value = null;
  if (!action.prompts || action.prompts.length === 0) {
    // Sem prompts: executa direto
    execute(action, {});
    return;
  }
  // Abre modal
  modalAction.value = action;
  promptValues.value = {};
  action.prompts.forEach(p => {
    promptValues.value[p.key] = p.type === 'enum' ? p.options?.[0] || '' : '';
  });
  nextTick(() => promptDialog.value?.open());
}

async function confirmModal() {
  if (executing.value || !modalAction.value) return;
  // Valida required
  const missing = modalAction.value.prompts.filter(p => {
    const value = promptValues.value[p.key];
    return (
      p.required &&
      (value === null ||
        value === undefined ||
        (typeof value === 'string' && !value.trim()))
    );
  });
  if (missing.length) {
    executeError.value = t('QUICK_ACTION_RUNNER.REQUIRED', {
      fields: missing.map(p => p.label).join(', '),
    });
    return;
  }
  await execute(modalAction.value, promptValues.value);
}

function openConfiguration() {
  if (!isAdmin.value || !accountId.value) return;
  router.push({
    name: 'kanban_board',
    params: { accountId: accountId.value },
    query: { view: 'quick-actions' },
  });
}
</script>

<template>
  <div v-if="!loading" class="quick-action-buttons space-y-2">
    <div
      v-if="loadError"
      role="alert"
      class="rounded-xl border border-n-ruby-8 bg-n-ruby-2 px-3 py-2 text-xs text-n-ruby-11"
    >
      <p class="m-0">{{ loadError }}</p>
      <button
        type="button"
        class="mt-1 text-woot-500 hover:underline"
        @click="load"
      >
        {{ t('QUICK_ACTION_RUNNER.RETRY') }}
      </button>
    </div>
    <!-- Empty state com CTA -->
    <div
      v-if="!loadError && !filtered.length && !hideEmptyState"
      class="text-xs text-n-slate-11 px-3 py-2 rounded-xl border border-dashed border-n-weak text-center"
    >
      <p class="mb-1">{{ t('QUICK_ACTION_RUNNER.EMPTY') }}</p>
      <button
        v-if="isAdmin"
        type="button"
        class="text-woot-500 hover:underline"
        @click="openConfiguration"
      >
        {{ t('QUICK_ACTION_RUNNER.CONFIGURE') }}
      </button>
    </div>
    <!-- Grid de botões -->
    <div class="grid grid-cols-1 gap-2">
      <button
        v-for="a in filtered"
        :key="a.id"
        type="button"
        class="flex min-h-11 items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition disabled:opacity-50 min-w-0"
        :class="colorClass(a)"
        :disabled="executing"
        @click="clickAction(a)"
      >
        <span class="text-base shrink-0">{{ a.icon }}</span>
        <span class="break-words text-left">{{ a.name }}</span>
      </button>
    </div>

    <p v-if="executeError" role="alert" class="m-0 text-sm text-rose-600">
      {{ executeError }}
    </p>

    <p
      v-if="lastResult"
      :role="lastResult.success ? 'status' : 'alert'"
      class="text-sm text-left"
      :class="lastResult.success ? 'text-emerald-600' : 'text-rose-600'"
    >
      <span
        aria-hidden="true"
        class="inline-block size-4 align-middle"
        :class="
          lastResult.success ? 'i-ph-check-circle' : 'i-ph-warning-circle'
        "
      />
      {{ lastResult.action }}
      <template v-if="lastResult.failedCount">
        <br />
        {{
          t('QUICK_ACTION_RUNNER.PARTIAL_RESULT', {
            failed: lastResult.failedCount,
            total: lastResult.totalCount,
          })
        }}
        <small class="mt-1 block text-sm text-n-slate-11">
          {{ t('QUICK_ACTION_RUNNER.PARTIAL_GUIDANCE') }}
        </small>
      </template>
    </p>

    <Dialog
      v-if="modalAction"
      ref="promptDialog"
      :title="modalAction.name"
      :confirm-button-label="t('QUICK_ACTION_RUNNER.CONFIRM')"
      :cancel-button-label="t('QUICK_ACTION_RUNNER.CANCEL')"
      :is-loading="executing"
      :disable-confirm-button="executing"
      confirm-before-close
      @request-close="closeModal"
      @confirm="confirmModal"
    >
      <div class="space-y-3">
        <header class="flex items-center gap-2">
          <span class="text-2xl">{{ modalAction.icon }}</span>
        </header>

        <p v-if="executeError" role="alert" class="m-0 text-sm text-n-ruby-11">
          {{ executeError }}
        </p>

        <div v-for="p in modalAction.prompts" :key="p.key">
          <label class="block text-xs font-medium text-n-slate-11 mb-1">
            {{ p.label }}<span v-if="p.required" class="text-rose-500">*</span>
          </label>
          <select
            v-if="p.type === 'enum'"
            v-model="promptValues[p.key]"
            :aria-label="p.label"
            :required="p.required"
            :disabled="executing"
            class="!mb-0 w-full min-h-11 rounded-xl border border-n-weak bg-n-solid-3 px-3 py-2 text-sm"
          >
            <option v-for="opt in p.options || []" :key="opt" :value="opt">
              {{ opt }}
            </option>
          </select>
          <input
            v-else-if="p.type === 'number'"
            v-model="promptValues[p.key]"
            :aria-label="p.label"
            :required="p.required"
            :disabled="executing"
            type="number"
            step="any"
            class="!mb-0 w-full min-h-11 rounded-xl border border-n-weak bg-n-solid-3 px-3 py-2 text-sm"
          />
          <input
            v-else
            v-model="promptValues[p.key]"
            :aria-label="p.label"
            :required="p.required"
            :disabled="executing"
            type="text"
            class="!mb-0 w-full min-h-11 rounded-xl border border-n-weak bg-n-solid-3 px-3 py-2 text-sm"
          />
        </div>
      </div>
    </Dialog>
  </div>
</template>

<style scoped>
.quick-action-buttons {
  animation: fade-in 0.2s ease-out;
}
@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
